import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Video, X } from "lucide-react";
import type {
  AiAssistOverviewFlag,
  ClinicalPhase,
  JournalSectionKey,
  MeetingDocumentation,
  MeetingLiveStatus,
  MeetingPrep,
  PatientAttentionTexts,
  PatientDetail,
  PatientFormFacts,
  TreatmentDecisionNoteTarget,
} from "@care-by-ella/contracts";

type MeetingNoteTarget = Extract<
  TreatmentDecisionNoteTarget,
  { kind: "meeting" }
>;
import { useAuth } from "@/auth/AuthContext";
import { MeetingRoom } from "@/components/MeetingRoom";
import { EvidenceSummaryCard } from "@/components/EvidenceSummaryCard";
import { RiskFlagsPanel } from "@/components/RiskFlagsPanel";
import { AttentionTextsPanel } from "@/components/AttentionTextsPanel";
import { AiAssistOverviewPanel } from "@/components/meeting/AiAssistOverviewPanel";
import { FlagsAndMissingBasis } from "@/components/meeting/FlagsAndMissingBasis";
import { MeetingSupportPanel } from "@/components/meeting/MeetingSupportPanel";
import { RecommendedCompletions } from "@/components/meeting/RecommendedCompletions";
import {
  pickerRecommendationNotes,
  type RecommendedCompletion,
} from "@/lib/recommended-completions";
import {
  MeetingJournalCard,
  type MeetingJournalHandle,
} from "@/components/meeting/MeetingJournalCard";
import { AiSummarisedHeadings } from "@/components/JournalSectionsCard";
import { autoSummaryProgressLabel } from "@/lib/journal-auto-summary";
import { HealthDeclarationPanel } from "@/components/meeting/HealthDeclarationPanel";
import { LowBmiAssessmentPanel } from "@/components/LowBmiAssessmentPanel";
import {
  assembleJournalText,
  emptyJournalHeadings,
  meetingJournalDecision,
  JOURNAL_SECTION_KEYS,
  meetingJoinability,
  assembleTreatmentDecisionNoteText,
  buildDoctorNoticePrefill,
  doctorNoticePersonalText,
  finalizeTreatmentComment,
  treatmentCommentPrefill,
  WHAT_HAPPENS_NEXT_HEADING,
} from "@care-by-ella/contracts";
import { TreatmentCommentField } from "@/components/TreatmentCommentField";
import {
  APPROVE_AND_PUBLISH_CTA,
  meetingPatientPackage,
} from "@/lib/meeting-patient-package";
import {
  COMPLETION_ITEMS,
  legacyNextStepTasks,
  type MeetingDocumentationRecord,
} from "@care-by-ella/contracts";
import {
  CompletionPicker,
  CompletionProgress,
  completionPayload,
  completionProblem,
  EMPTY_COMPLETION,
  type CompletionValue,
} from "@/components/CompletionPicker";
import {
  EMPTY_ORDER,
  orderComplete,
  orderCatalogueRef,
  orderDraftFrom,
  PrescriptionOrderFields,
  type OrderDraft,
} from "@/components/PrescriptionOrderFields";
import { PlannedMedicationExtraFields } from "@/components/PlannedMedicationExtraFields";
import {
  EMPTY_PLAN_CHOICE,
  planChoicePayload,
  type PlanChoiceValue,
} from "@/components/SubscriptionPlanChoice";
import { MeetingDecisionFields } from "@/components/meeting/MeetingDecisionFields";
import {
  meetingDecisionPlanProblem,
  meetingDecisionProblem,
  meetingNoteOrder,
  meetingShowsRecept,
} from "@/lib/meeting-decision";
import {
  MEETING_STEP_INSTRUCTION,
  MEETING_STEP_TITLE,
  meetingSteps,
  nextMeetingStep,
  previousMeetingStep,
  webdocTicksProblem,
  type MeetingStep,
} from "@/lib/meeting-steps";
import { useUnsavedJournal } from "@/components/UnsavedJournalGuard";
import { acuteAlarmHeading, headerRiskCounts } from "@/lib/attention-texts";
import { errorText } from "@/lib/error-text";
import {
  EMPTY_PRESCRIPTION_CHOICE,
  NEW_APPROVAL_CHOICES,
  prescriptionDecisionPayload,
  WebdocControlStep,
  type PrescriptionChoiceValue,
} from "@/components/WebdocControlStep";
import { DoctorNoticeField } from "@/components/DoctorNoticeField";
import {
  MeetingSummaryField,
  MeetingSummaryPublishedCard,
  useMeetingSummary,
} from "@/components/meeting/MeetingSummaryField";
import {
  meetingShowsSummary,
  meetingSummaryClaimErrors,
  meetingSummaryPrefillFrom,
} from "@/lib/meeting-summary";
import {
  CANNOT_PROCEED_EXPLANATION_PROBLEM,
  doctorNoticeClaimErrors,
  doctorNoticeText,
  meetingNoticePrefill,
} from "@/lib/doctor-notice";
import {
  canPublishMeeting,
  meetingNeedsLabOrder,
  meetingWebdocControl,
} from "@/lib/webdoc-step";
import { meetingReasonLabel } from "@/lib/meeting-labels";
import { formatElapsed } from "@/lib/meeting-view";
import { patientHref } from "@/lib/patient-links";
import {
  documentMeeting,
  documentMeetingAndDecide,
  endClinicianMeeting,
  fetchClinicianMeetingStatus,
  fetchMeetingPrep,
  markClinicianMeetingNoShow,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "@/components/SupportView";
import { BackupPhoneLine } from "./BackupPhoneLine";
import { useNow } from "@/lib/use-now";
import {
  meetingGoneNotice,
  type MeetingGoneNotice,
} from "@/lib/meeting-live-status";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";

// TES-1383 — how often the open meeting view re-reads the meeting's status.
const MEETING_STATUS_POLL_MS = 30_000;

// TES-291 [J10] — the managed care-meeting workflow screen.
//
// TES-955 (TES-941 "§VÅRDMÖTESVYN") — the meeting is a tab under the patient,
// no longer a route of its own. The patient page's header (PatientHeader, the
// clickable name, the decision entry point) is on top, so this component has
// no header and no facts column of its own:
//   "Patientheader överst", "En stor och tydlig videoyta", "Journalanteckningen bredvid", "Ingen separat vänsterspalt med duplicerad patientinformation"
// Two columns: the patient (a large video — she holds her phone in portrait,
// so in a call the pane takes the full height of the screen) and what the
// doctor writes (the note, then "Beslut"). Risk flags, handed-in evidence
// and the forms at a glance are one click away in a side sheet, the same
// components as on the overview; the full health declaration still slides
// in from the right on demand.
//
// What the design sketch shows and this deliberately does not: autosave and
// quick-reply chips — no API and no canonical copy; blood pressure / pulse
// tiles — the patient header carries the latest reading, and we do not invent
// numbers. The sketch's Recept block (medication, dose, length) is here since
// TES-956 as the structured proposed order; the prescription itself is still
// written in Webdoc.
//
// History: TES-506 made this one screen instead of a full-screen video
// overlay; TES-521 opened the form answers during the consult; TES-537
// turned the decision into the four "Nästa steg" choices; TES-950 replaced
// those fixed packages with the shared completion picker; TES-904 replaced
// the single "Utkast till journalanteckning" box and the plan box with the
// seven-heading journal (MeetingJournalCard); TES-1019 made the step after
// the bottom button (TES-1294: "Sammanställ journalanteckning") show, copy
// and confirm the finished note.
//
// TES-1316 (A-02 2026-10-13, "GUIDAT DOKUMENTATIONSFLÖDE EFTER FÖRSTA
// VÅRDMÖTET"; TES-1405, Ö-456's six steps) — the meeting is step 1.
// "Sammanställ journalanteckning" compiles the note and opens steps 2–6
// (the compiled note, outcome, plan and patient texts, Webdoc
// confirmations, preview and publish; MEETING_STEP_TITLE holds Ö-456's
// names) in the documentation column. The video column
// stays as it is, so the patient never disappears:
//   "Patienten får inte försvinna förrän läkaren själv avslutar mötet."
// Steps 2–6 keep their state in this component; only step 6's publish
// stores anything.

const PAYMENT_LABEL: Record<string, string> = {
  none: "Ingen betalning",
  pending: "Betalning väntar",
  paid: "Betald ✓",
  consumed: "Betald ✓",
};

/** mm:ss since `since`, ticking once a second while mounted. */
function MeetingTimer({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="inline-flex items-center gap-sm rounded-pill border border-surfaceMuted bg-surface px-md py-[6px] font-mono text-sm tabular-nums text-textPrimary">
      <span
        aria-hidden="true"
        className="h-[8px] w-[8px] rounded-full bg-notify"
      />
      {formatElapsed((now - since) / 1000)}
    </span>
  );
}

// TES-950 — the form reopened on a documented meeting. A meeting documented
// with the picker comes back as it was chosen; one documented with a legacy
// package comes back as the items that package asked for, so re-documenting it
// starts from what the patient actually has in front of her.
function completionFromRecord(
  doc: MeetingDocumentationRecord,
): CompletionValue {
  if (doc.completion) {
    // "Annan specificerad komplettering" already exists as her checklist row
    // (TES-1000); it is not reopened, so re-documenting does not open it twice.
    return {
      outcome: doc.completion.outcome,
      items: doc.completion.items.filter((i) => i !== "other"),
      otherText: "",
      otherHeading: "",
      otherUploadAllowed: false,
    };
  }
  if (!doc.nextStep) return EMPTY_COMPLETION;
  if (doc.nextStep === "cannot_proceed") {
    return { ...EMPTY_COMPLETION, outcome: "cannot_proceed" };
  }
  const tasks: string[] = legacyNextStepTasks(
    doc.nextStep,
    doc.prescriptionEvidenceRequired ?? false,
  );
  return {
    outcome: "completion",
    items: COMPLETION_ITEMS.filter((i) => tasks.includes(i)),
    otherText: "",
    otherHeading: "",
    otherUploadAllowed: false,
  };
}

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-lg bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06),0_8px_24px_-16px_rgba(97,32,95,0.25)] ${className}`}
    >
      {children}
    </section>
  );
}

function FieldLabel({
  children,
  hint,
}: {
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="mb-xs block text-xs font-semibold text-textSecondary">
      {children}
      {hint ? <span className="font-normal"> · {hint}</span> : null}
    </label>
  );
}

const TEXTAREA =
  "w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

/** The standing video column when there is no live call: the same dark
 *  card, telling the doctor where the meeting stands and offering the
 *  one action that applies. */
function VideoIdle({
  joinability,
  canJoin,
  documented,
  scheduledAt,
  backupPhone,
  gone,
  onJoin,
  noShow,
  onMarkNoShow,
  markingNoShow,
}: {
  // TES-1671 — the meeting was closed as a no-show; the "Patienten kom inte"
  // action is null when it does not apply (before the start, closed, or the
  // admin's read-only view).
  noShow: boolean;
  onMarkNoShow: (() => void) | null;
  markingNoShow: boolean;
  joinability: ReturnType<typeof meetingJoinability>;
  // TES-1383 — the meeting was cancelled or moved; replaces the join offer.
  gone: MeetingGoneNotice | null;
  // False in the admin's read-only view, where the call is not hers to join.
  canJoin: boolean;
  documented: boolean;
  scheduledAt: string;
  // TES-1306 — undefined in the admin's read-only view (see BackupPhoneLine).
  backupPhone: string | null | undefined;
  onJoin: () => void;
}) {
  const title = gone
    ? gone.link
      ? "Mötet är ombokat"
      : "Mötet är avbokat"
    : noShow
      ? "Patienten kom inte"
      : joinability === "open"
        ? "Redo att ansluta"
        : joinability === "over"
          ? documented
            ? "Mötet är dokumenterat"
            : "Mötet har passerat"
          : "Mötet öppnar snart";
  const body = gone
    ? gone.text
    : joinability === "open"
      ? "Patienten ansluter från sin telefon. Videon visas här, stående."
      : joinability === "over"
        ? documented
          ? "Anteckningen finns till höger och på patientens sida."
          : "Dokumentera mötet till höger."
        : `Bokat ${fmt(scheduledAt)}. Kan anslutas 15 minuter före starttiden.`;
  return (
    // TES-1300 — as tall as its content, with the button straight under the
    // title. On 2026-10-13 the button was the last item of a tall, sticky 4:3
    // card and sat below a MacBook's screen edge, under the Dock, while the
    // patient waited in the call: the page could not scroll it into view.
    <div className="flex flex-col items-center gap-md rounded-lg bg-[radial-gradient(120%_80%_at_50%_0%,#5a1d58_0%,#2a1128_60%,#1d0b1c_100%)] px-lg py-xl text-center text-white shadow-[0_24px_48px_-24px_rgba(42,26,40,0.6)]">
      <span
        aria-hidden="true"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10"
      >
        <Video size={22} />
      </span>
      <h2 className="font-display text-xl">{title}</h2>
      {gone?.link ? (
        <Link
          to={gone.link.href}
          className="rounded-pill bg-white px-lg py-[10px] text-sm font-semibold text-brand-deep no-underline hover:opacity-90"
        >
          {gone.link.label}
        </Link>
      ) : canJoin && !gone ? (
        <button
          type="button"
          onClick={onJoin}
          className="rounded-pill bg-white px-lg py-[10px] text-sm font-semibold text-brand-deep hover:opacity-90"
        >
          Anslut till mötet
        </button>
      ) : null}
      <p className="max-w-[30ch] text-sm text-white/70">{body}</p>
      {onMarkNoShow ? (
        <button
          type="button"
          onClick={onMarkNoShow}
          disabled={markingNoShow}
          className="rounded-pill border border-white/40 px-lg py-[8px] text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
        >
          {/* Proposed label (TES-1671), pending Emelie's OK. */}
          Patienten kom inte
        </button>
      ) : null}
      {joinability === "over" || gone ? null : (
        <BackupPhoneLine backupPhone={backupPhone} tone="dark" />
      )}
    </div>
  );
}

export function MeetingWorkspace({
  patientId,
  meetingId,
  patient,
  visible,
  onBackToList,
  onDocumented,
  onCallChange,
  alarmActions,
}: {
  patientId: string;
  meetingId: string;
  // The record the patient page already holds (for the health declaration);
  // the prep read is audited as a patient read on its own.
  patient: PatientDetail;
  // TES-955 — the workspace stays mounted while the doctor looks at another
  // tab of the same patient, so an open call and an unsaved note survive a
  // glance at the overview. Hidden, it keeps its state but does not mount
  // the side sheet's panels, which the overview already shows.
  visible: boolean;
  onBackToList: () => void;
  // The patient page re-reads the patient (header, meeting list, journey).
  onDocumented: () => void;
  onCallChange?: (inCall: boolean) => void;
  // "Mottaget" / "Avsluta larmet" on an alarm row, owned by the patient page.
  alarmActions: {
    busyAlarmId: string | null;
    onAcknowledge: (alarmId: string) => void;
    onClear: (alarmId: string) => void;
  };
}) {
  const { signOut } = useAuth();
  // TES-1040 — the admin's read-only view: the meeting and its note as they
  // stand. No video, no documenting, no alarm actions, no messages.
  const readOnly = useReadOnly();
  const now = useNow();
  const [prep, setPrep] = useState<MeetingPrep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<MeetingDocumentation | null>(null);
  // Bumped per save so the completion counter re-reads the new checklist.
  const [saveCount, setSaveCount] = useState(0);
  // Is the video room open? The room mounts/unmounts with this — leaving the
  // call tears down the Daily frame. `callKey` bumps to remount the room on
  // "Försök igen"; `inCallSince` feeds the header timer.
  const [inCallSince, setInCallSince] = useState<number | null>(null);
  const [callKey, setCallKey] = useState(0);
  // TES-978 — hanging up asks whether the meeting is over for both of them.
  const [endAsk, setEndAsk] = useState(false);
  const [ending, setEnding] = useState(false);
  // TES-1671 — "Patienten kom inte" in flight.
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  // TES-955 — "Riskflaggor och underlag", the side sheet. The flags come from
  // the forms-at-a-glance panel's one read, as on the overview (TES-948).
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  // TES-1019 — the flags and missing basis above the note: open by default,
  // and what the toolbar's flag count and acute alarm bring into view.
  const [flagsOpen, setFlagsOpen] = useState(true);
  // TES-1330 — the recommendations above the note, handed to the picker as
  // notes; re-read when the doctor re-assesses a reading in the card above.
  const [recommendations, setRecommendations] = useState<
    RecommendedCompletion[]
  >([]);
  const [evidenceReviewCount, setEvidenceReviewCount] = useState(0);
  const flagsRef = useRef<HTMLDetailsElement>(null);
  const showFlags = useCallback(() => {
    setFlagsOpen(true);
    flagsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  // TES-1001 — the free texts ride on the same read.
  const [formFacts, setFormFacts] = useState<{
    flags: AiAssistOverviewFlag[] | null;
    attention: PatientAttentionTexts | null;
    error: string | null;
  }>({ flags: null, attention: null, error: null });
  const onFormFacts = useCallback(
    (facts: PatientFormFacts | null, error: string | null) =>
      setFormFacts({
        flags: facts?.flags ?? null,
        // An older server without the field: an empty list, not "loading".
        attention: facts
          ? (facts.attention ?? { items: [], sources: [] })
          : null,
        error,
      }),
    [],
  );

  // Documentation form state. The note itself — assessment and plan
  // included — is the journal card's (TES-904); it is saved on its own and
  // read back from the card when the meeting is documented.
  const journalRef = useRef<MeetingJournalHandle>(null);
  // TES-950 — the shared completion picker. No outcome chosen = the doctor
  // has not decided, and the form will not submit.
  const [completion, setCompletion] =
    useState<CompletionValue>(EMPTY_COMPLETION);
  // TES-1316 — which of the five steps the documentation column shows.
  // Step 1 (meeting mode) stays mounted while steps 2–6 are shown, so the
  // journal card keeps its state. Only step 6's publish (step 5's for a
  // voluntary meeting) documents and publishes (TES-997, Ö-259).
  const [step, setStep] = useState<MeetingStep>(1);
  const columnRef = useRef<HTMLDivElement>(null);
  const [labOrderDone, setLabOrderDone] = useState(false);
  // TES-442 — "Remissnummer i Webdoc (valfritt)" beside the remiss tick.
  const [requisitionRef, setRequisitionRef] = useState("");
  // TES-1019 (Emelie, points 4–7) — the Webdoc step shows the finished
  // note, assembled from the saved boxes and the generated "Åtgärd och
  // behandlingsplan" (TES-1328) (no AI call), offers "Kopiera hela
  // journalanteckningen", and requires "Journalanteckningen är införd i
  // Webdoc" before publishing.
  const [journalDone, setJournalDone] = useState(false);
  const [preparing, setPreparing] = useState(false);
  // TES-1312 — the AI run before the step opens, and the headings it wrote.
  const [summaryProgress, setSummaryProgress] = useState<string | null>(null);
  const [aiHeadings, setAiHeadings] = useState<string[]>([]);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const noteRef = useRef<HTMLTextAreaElement>(null);
  // TES-956 — the order proposed at the meeting, entered once: the journal's
  // "Ordination:" line and the treatment decision both start from it. The
  // prescription itself is still written in Webdoc.
  const [order, setOrder] = useState<OrderDraft>(EMPTY_ORDER);
  // TES-1185 (TES-1180 point 1) — the rest of Emelie's planned medication:
  // "styrka" and "behandlingsplan". Saved with the order as one record.
  const [strength, setStrength] = useState("");
  const [plan, setPlan] = useState("");
  const plannedOrder = {
    ...order,
    ...(strength.trim() ? { strength: strength.trim() } : {}),
    ...(plan.trim() ? { plan: plan.trim() } : {}),
  };
  const [handoffReason, setHandoffReason] = useState("");
  // TES-1201 — "Läkarens besked till patienten · visas under Din vård",
  // in the Webdoc step. Null until the doctor types in it: the text then
  // follows the prefill, so a ticked remiss brings its line in.
  const [notice, setNotice] = useState<string | null>(null);
  // TES-1290 — the one-step approval's comment; null follows the prefill.
  const [comment, setComment] = useState<string | null>(null);
  // TES-1185 (TES-1180 points 1 and 4) — no kompletteringar: the positive
  // decision is taken in the same flow. TES-1400 (Ö-526) — always: "Godkänd
  // för behandling" is the outcome itself, so there is no "decide later"
  // box any more. The boxes compiled by "Sammanställ journalanteckning" are
  // kept so the note can be shown with the outcome and decision chosen
  // after it.
  const [webdocSections, setWebdocSections] = useState<
    { key: JournalSectionKey; content: string }[] | null
  >(null);
  const [clinicalPhase, setClinicalPhase] = useState<ClinicalPhase | null>(
    null,
  );
  const [followUpConfirmed, setFollowUpConfirmed] = useState(false);
  const [subscription, setSubscription] =
    useState<PlanChoiceValue>(EMPTY_PLAN_CHOICE);
  const [decisionPrescription, setDecisionPrescription] =
    useState<PrescriptionChoiceValue>(EMPTY_PRESCRIPTION_CHOICE);
  // Has the doctor touched the form? The server's prefill must never overwrite
  // what she has just written — otherwise the reload after documenting could
  // throw away text that has not been saved yet.
  const [dirty, setDirty] = useState(false);
  // TES-1019 — whether the AI switch is on here (the forms overview's first
  // request says); the note offers its per-heading AI help only then.
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null);
  const onUnauthorized = useCallback(() => void signOut(), [signOut]);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      // The API answers with a code (patient_erased, meeting_not_found …);
      // the doctor reads Swedish, or the code itself if we have no words.
      setError(errorText(e instanceof Error ? e.message : String(e)));
    },
    [signOut],
  );

  const load = useCallback(() => {
    fetchMeetingPrep(patientId, meetingId).then(setPrep).catch(handleError);
  }, [patientId, meetingId, handleError]);

  useEffect(load, [load]);

  // TES-1383 — the meeting's status, re-read while the view is open: on
  // open, when the doctor comes back to the tab, every 30 seconds, and right
  // before "Anslut". A failed read keeps the last answer; the poll is not
  // worth an error on the doctor's screen.
  const [liveStatus, setLiveStatus] = useState<MeetingLiveStatus | null>(null);
  const checkStatus = useCallback(async () => {
    try {
      const next = await fetchClinicianMeetingStatus(meetingId);
      setLiveStatus(next);
      return next;
    } catch (e) {
      if (e instanceof UnauthorizedError) void signOut();
      return null;
    }
  }, [meetingId, signOut]);
  useEffect(() => setLiveStatus(null), [meetingId]);
  useEffect(() => {
    if (!visible) return undefined;
    void checkStatus();
    const id = setInterval(() => void checkStatus(), MEETING_STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [checkStatus, visible]);
  useRefreshOnFocus(() => void checkStatus(), visible);
  const gone = liveStatus ? meetingGoneNotice(liveStatus) : null;
  // Cancelled while open: re-read the meeting and the patient page (its
  // meeting list), so the rest of the screen agrees with the notice.
  // Once per meeting.
  const goneNow = gone != null;
  const prepStatus = prep?.meeting.status;
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!goneNow || !prepStatus || prepStatus === "cancelled") return;
    if (refreshedFor.current === meetingId) return;
    refreshedFor.current = meetingId;
    load();
    onDocumented();
  }, [goneNow, prepStatus, meetingId, load, onDocumented]);

  async function join() {
    const now = await checkStatus();
    if (now && now.status === "cancelled") return;
    setInCallSince(Date.now());
  }

  const inCall = inCallSince != null;
  useEffect(() => {
    onCallChange?.(inCall);
  }, [inCall, onCallChange]);
  // Leaving the workspace (another meeting, back to the list) ends the call.
  useEffect(() => () => onCallChange?.(false), [onCallChange]);

  useEffect(() => {
    if (!evidenceOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEvidenceOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [evidenceOpen]);

  // TES-506 — open the form prefilled with what is already in the journal.
  // The doctor's notice is not read back: it is published as written, and
  // putting yesterday's text back in the box would invite publishing it a
  // second time (TES-1201: a re-documentation starts it empty).
  const documentation = prep?.documentation ?? null;
  useEffect(() => {
    if (!documentation || dirty) return;
    setCompletion(completionFromRecord(documentation));
    setOrder(
      documentation.proposedOrder
        ? orderDraftFrom(documentation.proposedOrder)
        : EMPTY_ORDER,
    );
    setStrength(documentation.proposedOrder?.strength ?? "");
    setPlan(documentation.proposedOrder?.plan ?? "");
  }, [documentation, dirty]);

  // TES-1228 — a voluntary meeting the patient booked herself is documented
  // with the journal note and the Webdoc confirmation only: no Recept, no
  // "Beslut" picker and no notice. The API refuses a decision for it.
  const voluntary = prep?.meeting.voluntary === true;
  const problem = voluntary ? null : completionProblem(completion);
  // TES-1314 — Recept follows the outcome: hidden until one is chosen and
  // for "Kan inte gå vidare", when no order is sent and the note states the
  // stored one.
  const showsRecept = !voluntary && meetingShowsRecept(completion.outcome);
  const noteOrder = meetingNoteOrder(
    completion.outcome,
    plannedOrder,
    documentation?.proposedOrder,
  );
  // The remiss line shows only when the picker asked for a new blood test.
  const needsLabOrder = !voluntary && meetingNeedsLabOrder(completion);
  const webdocStep = {
    journalDone,
    needsLabOrder,
    labOrderDone,
    requisitionRef,
  };
  // TES-1201 — the notice's prefill reads only what is confirmed: the remiss
  // tick; a meeting never confirms a prescription.
  const noticeConfirmed = {
    labOrder: needsLabOrder && labOrderDone,
    prescription: false,
  };
  // TES-1185 — with the decision taken in this step, the notice is the
  // decision's: the approval's prefill (the subscription link line).
  const noticePrefill =
    completion.outcome === "proceed" &&
    prep?.meeting.workflowStatus !== "documented"
      ? buildDoctorNoticePrefill({
          context: "meeting",
          decision: "approve",
          items: [],
          confirmed: noticeConfirmed,
        })
      : meetingNoticePrefill(
          { ...completion, items: completionPayload(completion).items },
          noticeConfirmed,
        );
  const noticeStartsEmpty = prep?.meeting.workflowStatus === "documented";
  const noticeBody = doctorNoticeText(notice, noticePrefill, noticeStartsEmpty);

  // TES-1185 — the one-step decision, offered when the meeting proceeds
  // with nothing to complete and has not been documented yet (a decision
  // after a documented meeting is the decision workspace's).
  const documentedAlready = prep?.meeting.workflowStatus === "documented";
  const oneStep =
    !voluntary && completion.outcome === "proceed" && !documentedAlready;
  // TES-1289 — "Läkarens besked" now goes out only for "Kan inte gå vidare"
  // (Ö-529, answered 2026-10-15). For the other two outcomes the meeting's text is
  // "Sammanfattning efter vårdmötet" below, and the one-step decision's is
  // the comment (TES-1290).
  const noticeSent = !voluntary && completion.outcome === "cannot_proceed";

  // TES-1289 — "Sammanfattning efter vårdmötet", the server's draft. Its
  // prefill follows the choices in the steps, which are saved only at the
  // publish; the remiss counts once ticked here or documented before.
  const showsSummary = meetingShowsSummary(completion.outcome, voluntary);
  const summaryLabConfirmed =
    (needsLabOrder && labOrderDone) ||
    (documentedAlready &&
      documentation?.completion?.outcome === "completion" &&
      documentation.completion.items.includes("new_blood_test"));
  const summaryItems = completionPayload(completion);
  const summary = useMeetingSummary({
    patientId,
    meetingId,
    enabled: prep !== null && !voluntary && !readOnly,
    prefillFor: (approvedOnServer) =>
      meetingSummaryPrefillFrom({
        outcome: completion.outcome,
        approved: oneStep || approvedOnServer,
        items: summaryItems.items,
        otherHeading:
          "otherHeading" in summaryItems
            ? summaryItems.otherHeading
            : undefined,
        order,
        labOrderConfirmed: summaryLabConfirmed,
      }),
    onUnauthorized,
  });
  const summaryBlocked =
    showsSummary &&
    meetingSummaryClaimErrors(summary.shown.text, summaryLabConfirmed).length >
      0;
  // TES-1290 (TES-1311) — "Läkarens kommentar till behandlingsbeslutet":
  // only on "Godkänd för behandling" taken here, never on a komplettering
  // or "Kan inte gå vidare". Published as the decision's text
  // (patientSummary); step 6 (TES-1317) previews `decisionCommentText`.
  const commentPrefill = treatmentCommentPrefill(oneStep ? "approve" : null);
  const commentText = doctorNoticeText(comment, commentPrefill);
  const decisionCommentText = oneStep
    ? finalizeTreatmentComment(commentText)
    : "";
  const followUpOutstanding = patient.followUpOutstanding ?? [];
  const planPayload = planChoicePayload(subscription);
  const prescriptionPayload = prescriptionDecisionPayload(decisionPrescription);
  const decisionPrescriptionChoice =
    prescriptionPayload && prescriptionPayload.choice !== "not_yet"
      ? {
          choice: prescriptionPayload.choice,
          ...(prescriptionPayload.existingValidUntil
            ? { existingValidUntil: prescriptionPayload.existingValidUntil }
            : {}),
        }
      : null;
  const decisionProblem = oneStep
    ? meetingDecisionProblem({
        orderComplete: orderComplete(order),
        clinicalPhase,
        followUpRemaining: followUpOutstanding.length > 0,
        followUpConfirmed,
        emailVerified: patient.patient.emailStatus === "verified",
        plan: planPayload,
        prescription: decisionPrescriptionChoice,
      })
    : null;
  // TES-1316 — the boxes are fixed when "Sammanställ journalanteckning"
  // compiles them; the outcome and the order are chosen after it, so the
  // note is assembled from them as they stand now.
  // TES-1058 (Emelie, point A) — the decision as journal text: the
  // outcome, and on a komplettering the items and the preliminary line.
  // TES-1228 — a voluntary meeting's note is the boxes alone, as the
  // server assembles it (no order, no decision stored).
  const journalNoteParts = {
    order: voluntary ? undefined : noteOrder,
    decision: voluntary
      ? null
      : meetingJournalDecision({
          outcome: completion.outcome,
          ...completionPayload(completion),
        }),
  };
  const webdocNote = webdocSections
    ? {
        text: assembleJournalText(webdocSections, journalNoteParts),
        skipped: emptyJournalHeadings(webdocSections, journalNoteParts),
      }
    : null;
  // The note the doctor copies: the meeting's, and with the decision taken
  // here, the decision's lines after it ("vårdmötes- och
  // beslutsanteckning"), exactly as the server compiles it.
  const shownNote =
    oneStep && webdocNote && webdocSections
      ? {
          text: assembleTreatmentDecisionNoteText(
            {
              kind: "meeting",
              meetingId,
              sections: webdocSections,
              order: plannedOrder,
              // The same decision the meeting's note writes; the target
              // schema's shape is the plain-array form of it.
              meetingDecision: meetingJournalDecision({
                outcome: completion.outcome,
                ...completionPayload(completion),
              }) as MeetingNoteTarget["meetingDecision"],
            },
            {
              outcome: "approve",
              ...(decisionPrescriptionChoice
                ? { prescription: decisionPrescriptionChoice }
                : {}),
            },
          ),
          skipped: webdocNote.skipped,
        }
      : webdocNote;
  // A change of the note after the journal tick takes the tick back.
  const shownText = shownNote?.text ?? null;
  const tickedText = useRef<string | null>(null);
  useEffect(() => {
    if (journalDone && tickedText.current !== shownText) {
      setJournalDone(false);
      setCopyState("idle");
    }
    // Only the text decides; the tick is read as it is.
  }, [shownText]);
  useEffect(() => {
    if (journalDone) tickedText.current = shownText;
  }, [journalDone]);

  // TES-1316 — once the note is compiled, the outcome, notice and ticks of
  // steps 2–6 exist only here until the publish. Leaving the meeting asks
  // first (the existing guard); a look at another tab does not, since the
  // workspace stays mounted there.
  useUnsavedJournal(
    !readOnly && webdocSections !== null ? "dirty" : "clean",
    "meeting",
  );

  function goToStep(next: MeetingStep) {
    setStep(next);
    columnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // TES-1019 / TES-1316 — "Sammanställ journalanteckning": save what is
  // still unsaved in the boxes first, so the note the later steps show is
  // the latest text, then leave meeting mode for step 2. It publishes
  // nothing and does not document the meeting (A-02: "Den publicerar inget
  // och markerar inte mötet som färdigdokumenterat").
  // TES-1312 — before that, every heading still holding her raw answers is
  // summarised by the AI; if any stays raw the card says so and the step
  // waits for the doctor ("Sammanställ ändå" comes back here with `force`).
  async function compose(force = false) {
    setPreparing(true);
    setError(null);
    try {
      if (!force) {
        const gate = await journalRef.current?.summariseRaw((current, total) =>
          setSummaryProgress(autoSummaryProgressLabel(current, total)),
        );
        setSummaryProgress(null);
        if (gate && gate.raw.length > 0) return;
      }
      const values = (await journalRef.current?.flush()) ?? {};
      setAiHeadings(journalRef.current?.aiSummarisedHeadings() ?? []);
      setWebdocSections(
        JOURNAL_SECTION_KEYS.map((key) => ({
          key,
          content: values[key] ?? "",
        })),
      );
      setJournalDone(false);
      setLabOrderDone(false);
      setCopyState("idle");
      goToStep(nextMeetingStep(1, voluntary)!);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError("Journalen kunde inte sparas. Försök igen.");
    } finally {
      setSummaryProgress(null);
      setPreparing(false);
    }
  }

  // "Kopiera hela journalanteckningen". Without clipboard access the text is
  // selected instead, for the doctor to copy by hand.
  async function copyNote() {
    if (!shownNote?.text) return;
    try {
      await navigator.clipboard.writeText(shownNote.text);
      setCopyState("copied");
    } catch {
      noteRef.current?.focus();
      noteRef.current?.select();
      setCopyState("failed");
    }
  }

  async function onDocument() {
    if (problem || (!voluntary && !completion.outcome)) return;
    setSaving(true);
    setError(null);
    try {
      // TES-904 — save the journal first; a failed save stops here rather
      // than closing the meeting on a note that is not stored.
      const journal = await journalRef.current?.flush();
      const meetingBody = {
        // TES-506: the assessment is optional. An empty field means nothing was
        // written, not "". TES-904: the two old columns are the journal's
        // "Bedömning" and "Åtgärd och behandlingsplan" during the transition,
        // so every reader of them keeps seeing what the doctor wrote.
        assessment: journal?.bedomning.trim() || undefined,
        treatmentPlanNote: journal?.atgard_behandlingsplan.trim() || undefined,
        // TES-1228 — a voluntary meeting sends neither a decision nor an
        // order; the API refuses both for it.
        ...(voluntary || !completion.outcome
          ? {}
          : {
              completion: {
                outcome: completion.outcome,
                ...completionPayload(completion),
              },
              // TES-956 — sent whenever Recept is shown, so emptying the
              // fields clears the order. The legacy free-text
              // `prescription` is no longer written.
              // TES-1314 — not sent while Recept is hidden ("Kan inte gå
              // vidare"); the API then keeps a stored order as it is.
              ...(showsRecept
                ? {
                    proposedOrder: {
                      medication: order.medication.trim(),
                      dose: order.dose.trim(),
                      frequency: order.frequency.trim(),
                      duration: order.duration.trim(),
                      // TES-1185 — always sent with the four, so emptying
                      // clears them.
                      strength: strength.trim(),
                      plan: plan.trim(),
                      // TES-1292 — the catalogue reference and a
                      // deviation's motivation.
                      ...orderCatalogueRef(order),
                    },
                  }
                : {}),
            }),
        coachHandoff: handoffReason.trim()
          ? { reason: handoffReason.trim() }
          : undefined,
        // TES-1019 — the confirmations ticked in the step. The API refuses
        // the publish without the journal confirmation, and stores the
        // note's final version and receipt with it.
        webdocControl: meetingWebdocControl(webdocStep),
      };
      const result = oneStep
        ? (
            await documentMeetingAndDecide(patientId, meetingId, {
              meeting: meetingBody,
              // TES-1185 — the decision in the same act, on the note he
              // copied; the notice goes with the decision.
              decision: {
                approval: {
                  medication: order.medication.trim(),
                  dose: order.dose.trim(),
                  frequency: order.frequency.trim(),
                  ...(order.duration.trim()
                    ? { duration: order.duration.trim() }
                    : {}),
                  clinicalPhase: clinicalPhase!,
                  ...(followUpOutstanding.length > 0
                    ? { withFollowUpRemaining: true }
                    : {}),
                },
                subscription: planPayload!,
                prescription: decisionPrescriptionChoice!,
                ...(decisionCommentText
                  ? { patientSummary: decisionCommentText }
                  : {}),
                noteText: shownNote?.text ?? "",
              },
            })
          ).meeting
        : await documentMeeting(patientId, meetingId, {
            ...meetingBody,
            // TES-1201 — published under Din vård; the API drops the
            // headings left empty and refuses a claim the ticks do not
            // confirm.
            patientNotice: noticeSent
              ? noticeBody.trim() || undefined
              : undefined,
          });
      setSaved(result);
      // TES-1289 — the summary is its own publish, after the
      // documentation: the text exactly as step 6 showed it.
      // The coordinator's rule (2026-10-14): with no text of hers the
      // generated "Vad händer nu?" is published on its own.
      if (showsSummary && patientPackage.noticeText) {
        try {
          await summary.publish();
        } catch (e) {
          if (e instanceof UnauthorizedError) throw e;
          setError(
            "Mötet är dokumenterat, men sammanfattningen kunde inte publiceras. Öppna stegen igen och publicera.",
          );
        }
      }
      // TES-1316 — back to meeting mode, where "Utfört" says what happened.
      // TES-1686 — scrolled to its top, where the published summary is
      // confirmed.
      goToStep(1);
      setLabOrderDone(false);
      setJournalDone(false);
      setWebdocSections(null);
      setSaveCount((n) => n + 1);
      // Published is published — a second "Dokumentera" starts the notice
      // empty rather than publishing the same text again.
      setNotice(null);
      setComment(null);
      setHandoffReason("");
      setDirty(false);
      load();
      onDocumented();
    } catch (e) {
      handleError(e);
    } finally {
      setSaving(false);
    }
  }

  // TES-978 (Simon 2026-09-28: "Ask him on leave") — leaving the call used
  // to leave only the room; the session ended when Daily's webhook or his
  // documentation got there, so the patient's app kept offering "Gå med"
  // in between. Whichever way he hangs up (Daily's button or the header's
  // "Lämna mötet"), the call is torn down and, while the meeting is still
  // live, he is asked whether it is over for both of them.
  //
  // TES-1671 — "live" is in_progress: only the patient joining starts the
  // meeting now, so a meeting still scheduled had nobody to end it for. The
  // status is re-read here, since she may have joined after the view loaded;
  // if she never came, "Patienten kom inte" on the video card closes it.
  async function hangUp() {
    setInCallSince(null);
    const live = await checkStatus();
    if ((live?.status ?? prep?.meeting.status) === "in_progress") {
      setEndAsk(true);
    }
  }

  async function markNoShow() {
    // Proposed copy (TES-1671), pending Emelie's OK.
    if (
      !window.confirm(
        "Markera att patienten inte kom? Mötet avslutas och patienten kan inte längre ansluta.",
      )
    ) {
      return;
    }
    setMarkingNoShow(true);
    setError(null);
    try {
      await markClinicianMeetingNoShow(meetingId);
      load();
      void checkStatus();
      onDocumented();
    } catch (e) {
      handleError(e);
    } finally {
      setMarkingNoShow(false);
    }
  }

  async function endForBoth() {
    setEnding(true);
    setError(null);
    try {
      await endClinicianMeeting(meetingId);
      setEndAsk(false);
      load();
      onDocumented();
    } catch (e) {
      setEndAsk(false);
      handleError(e);
    } finally {
      setEnding(false);
    }
  }

  const documented = prep?.meeting.workflowStatus === "documented";
  // TES-521 — the same window the API enforces for the token: at most 24 h
  // before the start, and never once the meeting is over or swept. A
  // no-show from eleven days ago used to keep offering "Anslut".
  // TES-1300 — re-read with the clock: a page opened before the 15-minute
  // lead used to keep "Mötet öppnar snart" until it was reloaded.
  const joinability = prep ? meetingJoinability(prep.meeting, now) : "over";
  const canJoin = joinability === "open" && !readOnly;
  // TES-1671 — "Patienten kom inte": the doctor's own (not the admin's
  // read-only view), from the scheduled start while the meeting is still
  // open and undocumented — the API's rule.
  const canMarkNoShow =
    !readOnly &&
    !documented &&
    !gone &&
    (prep?.meeting.status === "scheduled" ||
      prep?.meeting.status === "in_progress") &&
    now >= new Date(prep.meeting.scheduledAt).getTime();
  const patientLabel = prep
    ? (prep.patient.name ?? `…${prep.patient.personalNumberLast6}`)
    : "";

  // ---- panes -------------------------------------------------------------

  const videoPane = prep ? (
    inCall ? (
      <MeetingRoom
        key={callKey}
        meetingId={meetingId}
        label={patientLabel}
        // The meeting stays in the URL, so the call carries on while the
        // doctor reads the chat and comes back to it.
        chatHref={patientHref(patientId, "chat", meetingId)}
        onRetry={() => setCallKey((k) => k + 1)}
        onClose={() => void hangUp()}
      />
    ) : (
      <VideoIdle
        joinability={joinability}
        canJoin={canJoin}
        documented={documented}
        scheduledAt={prep.meeting.scheduledAt}
        backupPhone={prep.patient.backupPhone}
        gone={gone}
        onJoin={() => void join()}
        noShow={prep.meeting.status === "no_show"}
        onMarkNoShow={canMarkNoShow ? () => void markNoShow() : null}
        markingNoShow={markingNoShow}
      />
    )
  ) : null;

  // Always in view: the button to the next step sticks to the bottom of
  // the column, so it never drops below the fold when the form is taller than
  // the screen.
  // TES-1294 — A-02: "Det ska finnas en enda knapp längst ned efter samtliga
  // journalfält: SAMMANSTÄLL JOURNALANTECKNING". It opens the next step and
  // publishes nothing; the per-heading AI buttons are gone.
  const documentBar = (
    <div className="sticky bottom-0 -mx-[2px] bg-gradient-to-t from-background via-background/95 to-transparent px-[2px] pb-xs pt-lg">
      {/* TES-1316 — the outcome is step 3's now, so nothing here waits for
          it: the button only compiles the note and moves on. */}
      <button
        type="button"
        disabled={saving || preparing}
        onClick={() => void compose()}
        className="w-full rounded-pill bg-brand px-md py-[12px] text-sm font-semibold text-textOnBrand shadow-[0_8px_24px_-12px_rgba(97,32,95,0.6)] hover:opacity-90 disabled:opacity-50"
      >
        {summaryProgress
          ? summaryProgress
          : saving || preparing
            ? "Sparar…"
            : "Sammanställ journalanteckning"}
      </button>
      {/* TES-630 — the second time, none of what the button promised the first
          time happens: the meeting is already `completed`, so
          transitionWithLock does nothing, and journeyState.advance is monotonic
          and does not move the patient back or forward again. A button that
          still says "Avslutar mötet och startar patientens nästa steg"
          describes a different call from the one that happens. */}
      <p className="mt-xs text-center text-xs text-textSecondary">
        {documented
          ? "Mötet är redan avslutat — texten och besluten skrivs om"
          : "Publicerar inget och markerar inte mötet som färdigdokumenterat"}
      </p>
    </div>
  );

  // TES-956 (TES-941):
  //   "Receptinformationen ska bara registreras en gång."
  // The free-text box became the structured order, entered here once: the
  // journal states it as planned medication under "Åtgärd och
  // behandlingsplan" (TES-1328), and the treatment decision is prefilled
  // from it.
  // TES-1228 — not for a voluntary meeting: it plans no treatment.
  // TES-1314 / TES-1316 — step 4's, after an approval or a komplettering.
  const receptCard = (
    <Card>
      <div>
        <h3 className="font-display text-[17px] leading-6 text-textPrimary">
          Recept
        </h3>
        <div className="mt-xs">
          <PrescriptionOrderFields
            idPrefix="meeting-order"
            variant="draft"
            value={order}
            onChange={(next) => {
              setDirty(true);
              setOrder(next);
            }}
            disabled={saving || readOnly}
          />
        </div>
        {/* TES-1185 — "styrka" and "behandlingsplan", the two parts of the
          planned medication the four fields above do not hold. */}
        <div className="mt-sm">
          <PlannedMedicationExtraFields
            idPrefix="meeting-order"
            order={order}
            strength={strength}
            plan={plan}
            onStrengthChange={(v) => {
              setDirty(true);
              setStrength(v);
            }}
            onPlanChange={(v) => {
              setDirty(true);
              setPlan(v);
            }}
            disabled={saving || readOnly}
          />
        </div>
        <p className="mt-xs text-xs text-textSecondary">
          Sparas som den planerade läkemedelsbehandlingen. Förs in i journalen –
          som planerad och villkorad behandling när kompletteringar krävs – och
          förifyller behandlingsbeslutet. Själva receptet skrivs i Webdoc.
        </p>
        {/* A meeting documented before TES-956 carries its order as free
          text. Shown as written, read-only, while no structured order
          has been entered; the decision still parses it as a fallback. */}
        {documentation?.prescription && !documentation.proposedOrder ? (
          <div
            className="mt-sm rounded-md border border-surfaceMuted bg-surface p-sm"
            data-testid="legacy-prescription-note"
          >
            <p className="text-xs font-semibold text-textSecondary">
              Tidigare anteckning (fritext)
            </p>
            <p className="mt-xs whitespace-pre-wrap text-sm text-textPrimary">
              {documentation.prescription}
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );

  const documentationPane = (
    <div className="flex flex-col gap-md">
      {/* TES-1686 — what reached the patient, and when, from the server's
          stored publish; first in the column the publish
          returns to, and after a reload too. */}
      {summary.published ? (
        <MeetingSummaryPublishedCard published={summary.published} />
      ) : null}
      {/* TES-1019 (Emelie, point 2) — three levels: the flags and missing
          basis first, then the note's boxes, then each box's source. */}
      <FlagsAndMissingBasis
        ref={flagsRef}
        patientId={patientId}
        reloadKey={saveCount}
        open={flagsOpen}
        onToggle={setFlagsOpen}
        onUnauthorized={onUnauthorized}
        flags={
          <RiskFlagsPanel
            flags={formFacts.flags}
            flagsError={formFacts.error}
            sources={formFacts.attention?.sources}
            alarms={patient.riskFlags ?? []}
            patientGroup={patient.patientGroup}
            busyAlarmId={alarmActions.busyAlarmId}
            onAcknowledge={readOnly ? undefined : alarmActions.onAcknowledge}
            onClear={readOnly ? undefined : alarmActions.onClear}
          />
        }
      />
      {/* TES-1116 — BMI 25,0–26,9: WHtR and Kirill's possible indicators,
          next to the flags the doctor reads before the note. Nothing for a
          patient without F-02's low-BMI section. */}
      <LowBmiAssessmentPanel patientId={patientId} />
      {/* TES-1294 — operational lines only, apart from the note. */}
      {readOnly || documented ? null : (
        <MeetingSupportPanel
          voluntary={voluntary}
          outcome={completion.outcome}
          needsLabOrder={needsLabOrder}
        />
      )}
      {/* TES-1330 (TES-1327 §7) — "Ovanför journalen visas vilket underlag
          som har inkommit och, separat, Rekommenderade kompletteringar att ta
          ställning till." The same evidence card as on the overview and in
          the side sheet; the recommendations are a block of their own. */}
      <EvidenceSummaryCard
        patientId={patientId}
        onUnauthorized={onUnauthorized}
        onReviewChanged={() => setEvidenceReviewCount((n) => n + 1)}
      />
      <RecommendedCompletions
        patientId={patientId}
        reloadKey={saveCount + evidenceReviewCount}
        voluntary={voluntary}
        onRecommendations={setRecommendations}
        onUnauthorized={onUnauthorized}
      />
      <MeetingJournalCard
        ref={journalRef}
        patientId={patientId}
        meetingId={meetingId}
        onUnauthorized={onUnauthorized}
        order={noteOrder}
        aiAvailable={aiAvailable}
        onCompileAnyway={() => void compose(true)}
      />

      {/* TES-950 — administrative checks, the coach handover and messages to
          the patient are kept apart from the clinical decision above. TES-941:
          "Administrativa kontroller, coachöverlämning och meddelanden ska hållas åtskilda från den kliniska journalanteckningen." */}
      {readOnly ? null : (
        <Card>
          <h2 className="font-display text-[20px] leading-7 text-textPrimary">
            Administrativt och meddelanden
          </h2>
          {/* TES-997 (Ö-259) — "Muntlig abonnemangsinformation" is removed
            from doctor admin and must not block publishing. */}
          <div className="mt-sm">
            <FieldLabel hint="skapar handoff">
              Överlämning till coach
            </FieldLabel>
            <textarea
              value={handoffReason}
              onChange={(e) => setHandoffReason(e.target.value)}
              rows={2}
              className={TEXTAREA}
              placeholder="Anledning till överlämning…"
            />
          </div>
          {/* TES-1316 — the notice is step 4's ("Recept och patienttexter"). */}
          {voluntary ? null : (
            <p className="mt-md text-xs text-textSecondary">
              Läkarens besked till patienten skriver du efter Sammanställ
              journalanteckning, i steget Recept och patienttexter. Det visas
              under Din vård.
            </p>
          )}
        </Card>
      )}

      {saved ? (
        <Card className="border border-brand/30 bg-brand-accent/60">
          <h2 className="font-display text-[20px] leading-7 text-textPrimary">
            Utfört
          </h2>
          <ul className="mt-sm list-disc pl-lg text-sm text-textSecondary">
            <li>
              Mötet:{" "}
              {saved.actuated.meetingCompleted ? "avslutat" : "oförändrat"}
            </li>
            <li>Resa: {saved.actuated.journeyAdvancedTo ?? "oförändrad"}</li>
            <li>
              Coach-överlämning:{" "}
              {saved.actuated.coachHandoffId ? "skapad" : "—"}
            </li>
            <li>
              Läkarens besked:{" "}
              {saved.actuated.patientNoticeId
                ? "publicerat under Din vård"
                : "—"}
            </li>
            <li>
              Nästa flöde: {saved.actuated.nextFlowTriggered ? "utlöst" : "—"}
            </li>
          </ul>
        </Card>
      ) : null}
    </div>
  );

  // ---- steps 2–6 ---------------------------------------------------------

  const stepList = meetingSteps(voluntary);
  const nextStep = nextMeetingStep(step, voluntary);
  const prevStep = previousMeetingStep(step, voluntary);
  // What keeps the doctor in this step, shown on the disabled button the way
  // meeting mode's button used to show the picker's problem.
  const stepProblem: string | null =
    step === 3
      ? completion.outcome
        ? null
        : "Välj ett medicinskt utfall."
      : step === 4
        ? (problem ??
          // TES-1400 (Ö-529) — the API refuses the same.
          (noticeSent &&
          !documentedAlready &&
          !doctorNoticePersonalText(noticeBody)
            ? CANNOT_PROCEED_EXPLANATION_PROBLEM
            : null) ??
          (oneStep
            ? meetingDecisionPlanProblem({
                orderComplete: orderComplete(order),
                clinicalPhase,
                followUpRemaining: followUpOutstanding.length > 0,
                followUpConfirmed,
                emailVerified: patient.patient.emailStatus === "verified",
                plan: planPayload,
              })
            : null))
        : step === 5
          ? webdocTicksProblem({
              eRecept: oneStep,
              eReceptDone: decisionPrescriptionChoice !== null,
              labOrder: needsLabOrder,
              labOrderDone,
              journalDone,
            })
          : null;
  // TES-1317 — step 6's package: the notice with its generated
  // "Vad händer nu?", the decision's comment, and the contradiction
  // warnings (warn only). The server recomputes the generated part at
  // publish from the same facts.
  const patientPackage = meetingPatientPackage({
    voluntary,
    outcome: completion.outcome,
    decidesNow: oneStep,
    // TES-1289 — for proceed and komplettering the published text is the
    // summary; one already published unchanged is not sent again, exactly
    // as MeetingSummaryService.publish decides.
    documentedAlready:
      documentedAlready ||
      (showsSummary && summary.shown.unchangedSincePublish),
    noticeBody: showsSummary
      ? summary.shown.unchangedSincePublish
        ? ""
        : summary.publishedText
      : noticeSent
        ? noticeBody
        : "",
    items: completionPayload(completion).items,
    labOrderConfirmed: noticeConfirmed.labOrder,
    // The header's standing: entitled (trialing or active) or not.
    subscriptionActive:
      patient.subscription !== undefined &&
      patient.subscription.standing !== "inactive",
    commentText: decisionCommentText,
  });
  const noticeClaims = !(oneStep || noticeSent)
    ? []
    : doctorNoticeClaimErrors(
        oneStep ? commentText : noticeBody,
        noticeConfirmed,
      );

  // The note, read-only. In step 2 it is the compiled note; in step 4 it is
  // the preview ("slutlig journalförhandsvisning"); in step 5 it is what is
  // copied into Webdoc.
  const notePane = (copyable: boolean) =>
    shownNote?.text ? (
      <div className="flex flex-col gap-xs">
        {/* TES-1312 — beside the note, never in it. */}
        <AiSummarisedHeadings headings={aiHeadings} />
        <textarea
          ref={copyable ? noteRef : undefined}
          readOnly
          value={shownNote.text}
          rows={Math.min(18, shownNote.text.split("\n").length + 1)}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={
            copyable ? "Journalanteckning för Webdoc" : "Journalförhandsvisning"
          }
          className={`font-mono text-xs ${TEXTAREA}`}
        />
        {shownNote.skipped.length > 0 ? (
          <p className="text-xs text-textSecondary">
            {`Tomma rubriker togs inte med: ${shownNote.skipped.join(", ")}.`}
          </p>
        ) : null}
        {copyable ? (
          <>
            <button
              type="button"
              onClick={() => void copyNote()}
              className="self-start rounded-md bg-brand px-md py-sm text-sm font-semibold text-textOnBrand hover:opacity-90"
            >
              Kopiera hela journalanteckningen
            </button>
            {copyState !== "idle" ? (
              <p className="text-xs text-textSecondary" aria-live="polite">
                {copyState === "copied"
                  ? "Kopierad — klistra in i Webdoc."
                  : "Kunde inte kopiera automatiskt. Texten är markerad — kopiera den och klistra in i Webdoc."}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    ) : (
      <p className="text-sm text-textSecondary">
        Inget att kopiera — alla rubriker är tomma.
      </p>
    );

  const summaryCard = showsSummary ? (
    <Card>
      <MeetingSummaryField
        shown={summary.shown}
        labOrderConfirmed={summaryLabConfirmed}
        error={summary.error}
        disabled={saving}
        onType={summary.onType}
        onApply={summary.onApply}
        onKeep={summary.onKeep}
      />
    </Card>
  ) : null;

  const noticeCard = !noticeSent ? null : (
    <Card>
      {/* TES-1316 (CP-09 2026-10-12: "Vid not_approved måste patientvänlig
          medicinsk motivering och fortsatt vårdväg finnas") — until
          TES-1289/1290 split the texts, the one notice carries them. */}
      {completion.outcome === "cannot_proceed" ? (
        <p className="mb-sm text-sm text-textSecondary">
          Skriv den medicinska motiveringen och den fortsatta vårdvägen under
          Medicinsk bedömning. Patienten läser beskedet under Din vård.
        </p>
      ) : null}
      <DoctorNoticeField
        id="meeting-doctor-notice"
        text={noticeBody}
        value={notice}
        prefill={noticePrefill}
        onChange={setNotice}
        confirmed={noticeConfirmed}
        startEmpty={noticeStartsEmpty}
        disabled={saving}
      />
      {/* TES-1317 — the generated part is not written here. */}
      <p className="mt-xs text-xs text-textSecondary">
        {WHAT_HAPPENS_NEXT_HEADING} skapas automatiskt från utfallet och visas i
        sista steget.
      </p>
    </Card>
  );

  // TES-1290 — the one-step approval's comment, in place of the notice.
  const decisionCommentCard = (
    <Card>
      <TreatmentCommentField
        id="meeting-decision-comment"
        text={commentText}
        value={comment}
        prefill={commentPrefill}
        onChange={setComment}
        structured={order}
        confirmed={noticeConfirmed}
        disabled={saving}
        preview={false}
      />
    </Card>
  );

  const stepContent =
    step === 2 ? (
      // TES-1405 (Ö-456, step 2 "sammanställ journal") — the note as
      // "Sammanställ journalanteckning" compiled it, read before the
      // outcome. The outcome's and the plan's lines join it in the later
      // steps.
      <Card>{notePane(false)}</Card>
    ) : step === 3 ? (
      <Card>
        {/* TES-950 / TES-1314 — the shared completion picker's outcome,
            alone: the komplettering list is step 4's. */}
        <CompletionPicker
          patientId={patientId}
          value={completion}
          showItems={false}
          onChange={(next) => {
            setDirty(true);
            setCompletion(next);
          }}
          disabled={saving}
          onUnauthorized={() => void signOut()}
        />
        {/* "Kompletteringar: n av m klara" for the completion already open. */}
        <div className="mt-sm">
          <CompletionProgress
            patientId={patientId}
            reloadKey={saveCount}
            onUnauthorized={() => void signOut()}
          />
        </div>
      </Card>
    ) : step === 4 ? (
      // A-02 STEG 3 (Ö-456's step 4) — only what the chosen outcome needs.
      // The other outcomes' values stay in state, unpublished, until the
      // publish.
      <>
        {completion.outcome === "completion" ? (
          <Card>
            <CompletionPicker
              patientId={patientId}
              value={completion}
              showOutcome={false}
              recommendationNotes={pickerRecommendationNotes(recommendations)}
              onChange={(next) => {
                setDirty(true);
                setCompletion(next);
              }}
              disabled={saving}
              onUnauthorized={() => void signOut()}
            />
          </Card>
        ) : null}
        {showsRecept ? receptCard : null}
        {/* TES-1185 — the direct approval's own parts. TES-1400 (Ö-526):
            always taken here; a meeting documented before is decided on the
            patient's overview, as it was. */}
        {completion.outcome === "proceed" && documentedAlready ? (
          <Card>
            <p className="text-sm text-textSecondary">
              Mötet är redan dokumenterat. Du fattar behandlingsbeslutet på
              patientens översikt.
            </p>
          </Card>
        ) : null}
        {completion.outcome === "proceed" && !documentedAlready ? (
          <Card>
            <MeetingDecisionFields
              patientId={patientId}
              clinicalPhase={clinicalPhase}
              onClinicalPhaseChange={setClinicalPhase}
              followUp={followUpOutstanding}
              followUpConfirmed={followUpConfirmed}
              onFollowUpConfirmedChange={setFollowUpConfirmed}
              subscription={subscription}
              onSubscriptionChange={setSubscription}
              emailVerified={patient.patient.emailStatus === "verified"}
              disabled={saving}
            />
          </Card>
        ) : null}
        {summaryCard}
        {oneStep ? decisionCommentCard : noticeCard}
        {completion.outcome === "proceed" ||
        completion.outcome === "cannot_proceed" ? (
          <Card>
            <h3 className="mb-xs font-display text-[17px] leading-6 text-textPrimary">
              Journalförhandsvisning
            </h3>
            {notePane(false)}
          </Card>
        ) : null}
      </>
    ) : step === 5 ? (
      <Card className="flex flex-col gap-md">
        {notePane(true)}
        <WebdocControlStep
          idPrefix="meeting"
          journal={{ done: journalDone, onChange: setJournalDone }}
          {...(oneStep
            ? {
                decisionPrescription: true,
                // TES-1315 — the direct approval issues a new e-recept.
                decisionChoices: NEW_APPROVAL_CHOICES,
                prescription: decisionPrescription,
                onPrescriptionChange: setDecisionPrescription,
              }
            : {})}
          labOrder={needsLabOrder}
          labOrderDone={labOrderDone}
          onLabOrderDoneChange={setLabOrderDone}
          requisitionRef={{
            value: requisitionRef,
            onChange: setRequisitionRef,
          }}
          disabled={saving}
        />
      </Card>
    ) : (
      // Step 6 — TES-1317: exactly what the patient receives, as she will
      // read it, and no journal text (A-02's clarification of 2026-10-13).
      <Card className="flex flex-col gap-md">
        <h3 className="font-display text-[17px] leading-6 text-textPrimary">
          Det här publiceras till patienten
        </h3>
        {patientPackage.noticeText ? (
          <section aria-labelledby="meeting-publish-summary">
            <p
              id="meeting-publish-summary"
              className="text-xs font-semibold text-textSecondary"
            >
              Sammanfattning efter vårdmötet · visas under Din vård
            </p>
            {/* One notice: the doctor's text, then the generated block, in
                the order and with the line breaks she receives them. */}
            <div
              className="mt-xs rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary"
              data-testid="meeting-publish-notice"
            >
              {patientPackage.summaryText ? (
                <p className="whitespace-pre-wrap">
                  {patientPackage.summaryText}
                </p>
              ) : null}
              {patientPackage.whatHappensNext.length > 0 ? (
                <div
                  className={
                    patientPackage.summaryText
                      ? "mt-sm border-t border-surfaceMuted pt-sm"
                      : undefined
                  }
                  data-testid="meeting-publish-what-happens-next"
                >
                  <p className="flex flex-wrap items-baseline gap-xs">
                    <span>{WHAT_HAPPENS_NEXT_HEADING}</span>
                    <span className="text-xs text-textSecondary">
                      Skapas automatiskt från utfallet. Kontrollera att det
                      stämmer.
                    </span>
                  </p>
                  <p className="whitespace-pre-wrap">
                    {patientPackage.whatHappensNext.join("\n")}
                  </p>
                </div>
              ) : null}
            </div>
          </section>
        ) : (
          <p className="text-sm text-textSecondary">Inget besked publiceras.</p>
        )}
        {oneStep ? (
          <section aria-labelledby="meeting-publish-decision">
            <p
              id="meeting-publish-decision"
              className="text-xs font-semibold text-textSecondary"
            >
              Behandlingsbeslut
            </p>
            <div className="mt-xs rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary">
              <p className="font-semibold">Godkänd för behandling</p>
              <dl className="mt-xs grid grid-cols-[auto_1fr] gap-x-sm gap-y-[2px]">
                <dt className="text-textSecondary">Läkemedel</dt>
                <dd>
                  {[order.medication.trim(), strength.trim()]
                    .filter(Boolean)
                    .join(" ")}
                </dd>
                <dt className="text-textSecondary">Dos</dt>
                <dd>{order.dose.trim()}</dd>
                <dt className="text-textSecondary">Intervall</dt>
                <dd>{order.frequency.trim()}</dd>
              </dl>
              {/* A-02 "DIREKT GODKÄND EFTER FÖRSTA VÅRDMÖTET", point 3. */}
              <p className="mt-xs text-xs text-textSecondary">
                Läkemedel, dos och receptstatus visas under Din behandling efter
                verifierad abonnemangsaktivering. Abonnemangslänken skickas via
                e-post.
              </p>
            </div>
          </section>
        ) : null}
        {patientPackage.commentText ? (
          <section aria-labelledby="meeting-publish-comment">
            <p
              id="meeting-publish-comment"
              className="text-xs font-semibold text-textSecondary"
            >
              Läkarens kommentar till behandlingsbeslutet
            </p>
            <p className="mt-xs whitespace-pre-wrap rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary">
              {patientPackage.commentText}
            </p>
          </section>
        ) : null}
        {/* A-02: "Systemet ska varna vid motsägelser". A warning only: the
            doctor may publish after reading it. */}
        {patientPackage.warnings.length > 0 ? (
          <div
            role="status"
            className="rounded-md border border-warning/40 bg-warning/10 p-sm text-sm text-textPrimary"
            data-testid="meeting-publish-warnings"
          >
            <p className="font-semibold">Kontrollera innan du publicerar</p>
            <ul className="mt-xs list-disc pl-lg">
              {patientPackage.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => goToStep(3)}
              className="mt-xs text-sm font-semibold text-brand-deep underline"
            >
              Ändra texten
            </button>
          </div>
        ) : null}
        {noticeClaims.length > 0 ? (
          <div role="alert" className="text-sm text-danger">
            {noticeClaims.map((c) => (
              <p key={c}>{c}</p>
            ))}
            <button
              type="button"
              onClick={() => goToStep(3)}
              className="mt-xs text-sm font-semibold text-brand-deep underline"
            >
              Ändra beskedet
            </button>
          </div>
        ) : null}
        {decisionProblem ? (
          <p className="text-xs text-textSecondary" role="status">
            {decisionProblem}
          </p>
        ) : null}
      </Card>
    );

  const publishLabel = saving
    ? "Publicerar…"
    : voluntary
      ? documented
        ? "Uppdatera dokumentationen"
        : "Dokumentera och avsluta mötet"
      : // TES-1317 (CP-09) — every outcome's step 6 publishes with it.
        APPROVE_AND_PUBLISH_CTA;
  const publishBlocked = voluntary
    ? stepProblem !== null
    : !canPublishMeeting(webdocStep) ||
      noticeClaims.length > 0 ||
      summaryBlocked ||
      decisionProblem !== null;

  const postMeetingPane = (
    <div className="flex flex-col gap-md">
      <nav aria-label="Dokumentationssteg">
        <ol className="flex flex-wrap gap-xs">
          {stepList.map((s, i) => {
            const label = `${i + 1}. ${MEETING_STEP_TITLE[s]}`;
            const current = s === step;
            const earlier = s < step;
            return (
              <li key={s}>
                {earlier ? (
                  // "Tillbaka" to any earlier step; what was entered stays.
                  <button
                    type="button"
                    onClick={() => goToStep(s)}
                    disabled={saving}
                    className="rounded-pill border border-surfaceMuted bg-surface px-sm py-[4px] text-xs text-brand-deep hover:bg-brand/5"
                  >
                    {label}
                  </button>
                ) : (
                  <span
                    aria-current={current ? "step" : undefined}
                    className={`inline-block rounded-pill px-sm py-[4px] text-xs ${
                      current
                        ? "bg-brand font-semibold text-textOnBrand"
                        : "border border-surfaceMuted text-textDisabled"
                    }`}
                  >
                    {label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <div>
        <h2 className="font-display text-[22px] leading-7 text-textPrimary">
          {MEETING_STEP_TITLE[step]}
        </h2>
        <p className="mt-xs text-sm text-textSecondary">
          {MEETING_STEP_INSTRUCTION[step]}
        </p>
      </div>
      {stepContent}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="sticky bottom-0 -mx-[2px] flex flex-wrap items-center justify-between gap-sm bg-gradient-to-t from-background via-background/95 to-transparent px-[2px] pb-xs pt-lg">
        <button
          type="button"
          onClick={() => prevStep && goToStep(prevStep)}
          disabled={saving}
          className="rounded-pill px-md py-[12px] text-sm text-textSecondary hover:bg-surfaceMuted"
        >
          Tillbaka
        </button>
        {nextStep ? (
          <button
            type="button"
            onClick={() => goToStep(nextStep)}
            disabled={stepProblem !== null}
            className="flex-1 rounded-pill bg-brand px-md py-[12px] text-sm font-semibold text-textOnBrand shadow-[0_8px_24px_-12px_rgba(97,32,95,0.6)] hover:opacity-90 disabled:opacity-50"
          >
            {stepProblem ?? `Nästa: ${MEETING_STEP_TITLE[nextStep]}`}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void onDocument()}
            disabled={saving || publishBlocked}
            className="flex-1 rounded-pill bg-brand px-md py-[12px] text-sm font-semibold text-textOnBrand shadow-[0_8px_24px_-12px_rgba(97,32,95,0.6)] hover:opacity-90 disabled:opacity-50"
          >
            {voluntary && stepProblem ? stepProblem : publishLabel}
          </button>
        )}
      </div>
    </div>
  );

  // TES-1001 (Ö-250) — the same two numbers the patient header shows:
  // "Akutlarm: X" and "Riskflaggor: Y", one signal counted once. (This used
  // to add the open alarms on top of a formFlagCount that already held them,
  // counting each alarm twice.)
  const { acute: acuteCount, flags: flagCount } = headerRiskCounts({
    acuteAlarmCount: patient.acuteAlarmCount,
    formFlagCount: patient.formFlagCount,
    openAlarms: patient.riskFlags?.length ?? 0,
    fallbackFlagSum: patient.formSubmissions.reduce(
      (n, sub) => n + sub.flags.length + sub.hardStops.length,
      0,
    ),
  });

  const pill =
    "inline-flex items-center gap-xs rounded-pill border border-surfaceMuted bg-surface px-md py-[8px] text-sm font-semibold text-brand-deep hover:bg-brand/5";

  // The strip above the video: which meeting this is, and what the doctor
  // reaches for during it. Joining is the video card's own button — one
  // control per state, not one in each corner.
  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-sm">
        <button
          type="button"
          onClick={() => {
            if (
              inCall &&
              !window.confirm("Du lämnar samtalet om du går till listan.")
            ) {
              return;
            }
            onBackToList();
          }}
          className="text-sm text-textSecondary hover:text-textPrimary"
        >
          ← Alla vårdmöten
        </button>
        {prep ? (
          <>
            <span className="rounded-pill bg-peach px-sm py-[3px] text-[11px] font-semibold uppercase tracking-wide text-brand-deep">
              {meetingReasonLabel(prep.meeting.reason)}
            </span>
            <span className="text-sm text-textSecondary">
              {fmt(prep.meeting.scheduledAt)} ·{" "}
              {PAYMENT_LABEL[prep.payment.status] ?? prep.payment.status}
            </span>
          </>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-sm">
        {inCallSince != null ? <MeetingTimer since={inCallSince} /> : null}
        {/* TES-1019 — the flags sit above the note now; the count brings
            them into view, and the sheet keeps the rest of the basis. */}
        <button type="button" onClick={showFlags} className={pill}>
          Riskflaggor: {flagCount}
        </button>
        <button
          type="button"
          aria-expanded={evidenceOpen}
          aria-controls="meeting-evidence-sheet"
          onClick={() => setEvidenceOpen((v) => !v)}
          className={pill}
        >
          <ClipboardList size={16} aria-hidden="true" />
          Underlag
        </button>
        {acuteCount > 0 ? (
          <button
            type="button"
            onClick={showFlags}
            className="inline-flex items-center gap-xs rounded-pill bg-danger px-md py-[8px] text-sm font-semibold text-white hover:opacity-90"
          >
            {acuteAlarmHeading(acuteCount)}
          </button>
        ) : null}
        {prep ? (
          <button
            type="button"
            onClick={() => setDeclarationOpen(true)}
            className={pill}
          >
            Hälsodeklaration
          </button>
        ) : null}
        {/* TES-1306 — in a call the video card is the call itself, so the
            backup number sits here, beside "Lämna mötet". */}
        {inCall && prep ? (
          <BackupPhoneLine backupPhone={prep.patient.backupPhone} />
        ) : null}
        {inCall ? (
          <button
            type="button"
            onClick={() => void hangUp()}
            className="rounded-pill bg-brand px-md py-[8px] text-sm font-semibold text-textOnBrand hover:opacity-90"
          >
            Lämna mötet
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={visible ? "flex flex-col gap-md" : "hidden"}>
      {gone ? (
        <div
          role="alert"
          data-testid="meeting-gone-notice"
          className="flex flex-wrap items-center gap-sm rounded-md border border-warning/40 bg-warning/10 p-md text-sm font-semibold text-textPrimary"
        >
          <span>{gone.text}</span>
          {gone.link ? (
            <Link to={gone.link.href} className="text-brand underline">
              {gone.link.label}
            </Link>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-md border border-danger/30 bg-danger/10 p-md text-sm text-danger">
          {error}
        </div>
      ) : null}

      {!prep ? (
        <>
          {toolbar}
          {error ? null : <div className="text-textSecondary">Hämtar…</div>}
        </>
      ) : (
        // Patient | note — side by side, for the whole meeting. The video
        // column is sticky under the patient page's pinned header
        // (--patient-sticky-h, measured by the page), so the patient never
        // disappears upwards while the doctor scrolls the note. The video gets
        // the larger share; the note never narrower than it can be written in.
        <div className="grid grid-cols-1 gap-lg lg:grid-cols-[minmax(0,1.25fr)_minmax(360px,1fr)]">
          <div
            className="flex flex-col gap-sm lg:sticky lg:self-start"
            style={{ top: "calc(var(--patient-sticky-h, 0px) + 0.5rem)" }}
          >
            {toolbar}
            {/* In a call the video takes the height the screen has left under
                the patient header (the patient is in portrait, so height is
                what is scarce). Without a call it is a placeholder, and a
                placeholder that tall with an icon in the middle reads as a
                void. TES-1300: it is as tall as its card, so the join button
                can never sit below the screen edge (a 4:3 box put it under a
                laptop's Dock). */}
            <div
              className={inCall ? "min-h-[420px]" : "w-full"}
              style={
                inCall
                  ? {
                      height:
                        "calc(100vh - var(--patient-sticky-h, 0px) - 6rem)",
                    }
                  : undefined
              }
            >
              {videoPane}
            </div>
          </div>
          {/* TES-1316 — steps 2–6 replace this column, not the page. Step 1
              stays mounted (hidden) beneath them, so the journal card keeps
              what the doctor wrote while she goes back and forth. */}
          <div
            ref={columnRef}
            className="flex min-w-0 flex-col"
            style={{
              scrollMarginTop: "calc(var(--patient-sticky-h, 0px) + 0.5rem)",
            }}
          >
            <div className={step === 1 ? "flex flex-col" : "hidden"}>
              {documentationPane}
              {readOnly ? null : documentBar}
            </div>
            {step === 1 || readOnly ? null : postMeetingPane}
          </div>
        </div>
      )}

      {/* TES-955 — "Riskflaggor och inskickat underlag ska vara lätt tillgängliga inför och under mötet" (TES-941).
          A side sheet over the note, never over the video, and without a
          backdrop: the doctor keeps seeing the patient while she reads. The
          same components as on the overview — one list of flags, one card of
          evidence — not a second rendering of them.
          TES-1019 — the flags moved above the note (FlagsAndMissingBasis);
          the sheet keeps the free texts, the handed-in evidence and the
          forms at a glance, which also supplies the flags. */}
      {visible ? (
        <aside
          id="meeting-evidence-sheet"
          aria-label="Underlag"
          // A class, not the `hidden` attribute: `flex` would override it.
          className={`${evidenceOpen ? "flex" : "hidden"} fixed bottom-0 right-0 top-0 z-30 w-[min(34rem,100vw)] flex-col border-l border-surfaceMuted bg-background shadow-[-24px_0_48px_-24px_rgba(42,26,40,0.35)]`}
        >
          <header className="flex items-center justify-between gap-md border-b border-surfaceMuted bg-surface px-lg py-md">
            <h2 className="font-display text-[22px] leading-7 text-textPrimary">
              Underlag
            </h2>
            <button
              type="button"
              onClick={() => setEvidenceOpen(false)}
              aria-label="Stäng"
              className="rounded-pill border border-surfaceMuted p-[6px] text-textSecondary hover:text-textPrimary"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>
          <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto px-lg py-md">
            <AttentionTextsPanel
              attention={formFacts.attention}
              error={formFacts.error}
              patientGroup={patient.patientGroup}
            />
            <EvidenceSummaryCard
              patientId={patientId}
              onUnauthorized={() => void signOut()}
            />
            {/* TES-708/943 — the forms at a glance: facts and flags from the
                code whether or not AI_ASSIST_ENABLED is on; only the prose
                follows the switch. Its flags are in the list above the note,
                so it hands them up; mounted with the sheet closed too, since
                that list depends on it. */}
            <AiAssistOverviewPanel
              patientId={patientId}
              patientGroup={patient.patientGroup}
              onUnauthorized={() => void signOut()}
              onAvailability={setAiAvailable}
              hideFlags
              onFacts={onFormFacts}
            />
          </div>
        </aside>
      ) : null}

      {endAsk && visible ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-md">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="end-meeting-title"
            aria-describedby="end-meeting-body"
            className="flex w-full max-w-md flex-col gap-md rounded-md bg-surface p-lg"
          >
            <h3 id="end-meeting-title" className="font-display text-xl">
              Avsluta mötet för er båda?
            </h3>
            <p id="end-meeting-body" className="text-sm text-textSecondary">
              Mötet markeras som genomfört och patienten kan inte längre gå med.
              Du dokumenterar som vanligt efteråt. Lämnar du bara rummet kan ni
              båda ansluta igen.
            </p>
            <div className="flex flex-wrap justify-end gap-sm">
              <button
                type="button"
                onClick={() => setEndAsk(false)}
                disabled={ending}
                className="rounded-md px-md py-sm text-sm text-textSecondary hover:bg-surfaceMuted"
              >
                Lämna bara rummet
              </button>
              <button
                type="button"
                onClick={() => void endForBoth()}
                disabled={ending}
                className="rounded-md bg-brand px-md py-sm text-sm font-semibold text-textOnBrand hover:opacity-90 disabled:opacity-60"
              >
                {ending ? "Avslutar…" : "Avsluta mötet"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {prep && visible ? (
        <HealthDeclarationPanel
          prep={prep}
          patient={patient}
          open={declarationOpen}
          onClose={() => setDeclarationOpen(false)}
        />
      ) : null}
    </div>
  );
}
